import { useEffect, useRef } from 'react';
import { captureClientException } from './posthog';
import { invalidateDiamondAndGameQueries } from './diamondQueryInvalidation';
import { invalidateTournamentLiveQueries } from './tournamentQueryInvalidation';
import { invalidateSeatBoard } from './seatBoardApi';

type WebSocketMessage = {
  type: string;
  payload: unknown;
};

function readTournamentIdFromPayload(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const tournamentId = (payload as { tournamentId?: unknown }).tournamentId;
  return typeof tournamentId === 'string' && tournamentId.length > 0 ? tournamentId : undefined;
}

/** Routes that need live game/diamond/bracket updates. */
export function pathNeedsLiveSocket(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  if (path === '/') return false;
  return (
    path.startsWith('/tournament/') ||
    path.startsWith('/dashboard/') ||
    path.startsWith('/coach/') ||
    path.startsWith('/coach-') ||
    path.startsWith('/admin') ||
    path.startsWith('/org/') ||
    path.includes('/staffing') ||
    path.includes('/playoff')
  );
}

export function useWebSocket(enabled = true) {
  const ws = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;

    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = () => {
      if (disposed) return;

      let socket: WebSocket;
      try {
        socket = new WebSocket(wsUrl);
      } catch (error) {
        // Old WebKit throws synchronously when CSP blocks the connect. Degrade to
        // no live updates instead of taking the page down via the error boundary,
        // and stop retrying — a blocked or unsupported connect will not recover.
        captureClientException(error, { context: 'useWebSocket', wsUrl });
        return;
      }
      ws.current = socket;

      socket.onopen = () => {
        // console.log("WebSocket connected");
      };

      socket.onmessage = event => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);

          // Handle specific events
          if (message.type === 'game_updated') {
            invalidateTournamentLiveQueries(readTournamentIdFromPayload(message.payload));
          }

          if (message.type === 'diamond_updated') {
            invalidateDiamondAndGameQueries();
          }

          if (message.type === 'seats_updated') {
            const tournamentId = readTournamentIdFromPayload(message.payload);
            const divisionId = (message.payload as { divisionId?: unknown } | null)?.divisionId;
            if (tournamentId && typeof divisionId === 'string')
              invalidateSeatBoard(tournamentId, divisionId);
          }

          if (message.type === 'playoff_bracket_updated') {
            const tournamentId = readTournamentIdFromPayload(message.payload);
            invalidateTournamentLiveQueries(tournamentId);
          }
        } catch (error) {
          console.error('WebSocket message error:', error);
        }
      };

      socket.onclose = () => {
        if (disposed) return;
        // console.log("WebSocket disconnected, retrying in 5s...");
        retryTimer = setTimeout(connect, 5000);
      };
    };

    connect();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      ws.current?.close();
      ws.current = null;
    };
  }, [enabled]);
}
